
import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface NamesNumbersEditorProps {
  onClose: () => void;
}

const NamesNumbersEditor: React.FC<NamesNumbersEditorProps> = ({ onClose }) => {
  const [addNames, setAddNames] = useState(false);
  const [addNumbers, setAddNumbers] = useState(false);
  const [side, setSide] = useState("back");
  const [height, setHeight] = useState("2");
  const [color, setColor] = useState("black");
  const { toast } = useToast();
  
  const handleContinue = () => {
    toast({
      title: "Coming Soon!",
      description: "Names and numbers features will be available soon.",
    });
  };
  
  return (
    <div className="bg-white p-4 w-full max-w-md border rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Names and Numbers Tools</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>
      
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="font-medium">Step 1:</span>
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="addNames" 
                  checked={addNames} 
                  onCheckedChange={(checked) => setAddNames(!!checked)} 
                />
                <Label htmlFor="addNames">Add Names</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="addNumbers" 
                  checked={addNumbers} 
                  onCheckedChange={(checked) => setAddNumbers(!!checked)} 
                />
                <Label htmlFor="addNumbers">Add Numbers</Label>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="side" className="block mb-2">Side:</Label>
              <Select value={side} onValueChange={setSide}>
                <SelectTrigger id="side" className="w-full">
                  <SelectValue placeholder="Select Side" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="front">Front</SelectItem>
                  <SelectItem value="back">Back</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="height" className="block mb-2">Height:</Label>
              <div className="flex gap-2">
                <Select value={height} onValueChange={setHeight}>
                  <SelectTrigger id="height" className="w-full">
                    <SelectValue placeholder="Select Height" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 in</SelectItem>
                    <SelectItem value="2">2 in</SelectItem>
                    <SelectItem value="3">3 in</SelectItem>
                    <SelectItem value="4">4 in</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={height} onValueChange={setHeight}>
                  <SelectTrigger id="height" className="w-full">
                    <SelectValue placeholder="Select Height" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 in</SelectItem>
                    <SelectItem value="2">2 in</SelectItem>
                    <SelectItem value="3">3 in</SelectItem>
                    <SelectItem value="4">4 in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="color" className="block mb-2">Color:</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">Black</span>
                <div className="w-6 h-6 bg-black border border-gray-300 rounded"></div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="color" className="block mb-2">Color:</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">Black</span>
                <div className="w-6 h-6 bg-black border border-gray-300 rounded"></div>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={handleContinue} 
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!addNames && !addNumbers}
          >
            Step 2: Enter Names/Numbers
          </Button>
          
          <div className="mt-4">
            <div className="font-medium">Full list required for accurate pricing</div>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <div>
                <span>Names:</span> <span className="font-medium">$5.50 each item</span>
              </div>
              <div>
                <span>Numbers:</span> <span className="font-medium">$3.50 each item</span>
              </div>
            </div>
            
            <ul className="list-disc pl-5 mt-4 text-sm space-y-1">
              <li>'EXAMPLE' and '00' are sample placeholders</li>
              <li>Our artists will expertly place each name/number from your list</li>
              <li>Names/numbers may be printed or vinyl</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NamesNumbersEditor;
